import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  Eye,
  Save,
  Play,
  Pencil,
} from 'lucide-react';
import { leadCampaignsAPI, leadSegmentsAPI, leadsAPI } from '../utils/api';
import {
  LeadCampaignCreate,
  LeadCampaignStepResponse,
} from '../types/leadCampaign';
import LeadCampaignStateBadge from '../components/lead/LeadCampaignStateBadge';
import { usePermissions } from '../hooks/usePermissions';

type WizardStep = 1 | 2 | 3;

interface InfoForm {
  name: string;
  description: string;
  segment_id: string;
  from_email: string;
  from_name: string;
  reply_to: string;
}

const emptyInfo: InfoForm = {
  name: '',
  description: '',
  segment_id: '',
  from_email: '',
  from_name: '',
  reply_to: '',
};

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

export default function LeadCampaignEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');
  const isEdit = !!id;

  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [info, setInfo] = useState<InfoForm>(emptyInfo);

  const { data: existing, isLoading: isLoadingExisting } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getCampaign(id!);
      return res.data;
    },
    enabled: isEdit,
  });

  const { data: steps, isLoading: isLoadingSteps } = useQuery({
    queryKey: ['campaign-steps', id],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getSteps(id!);
      return res.data;
    },
    enabled: isEdit,
  });

  const { data: segments } = useQuery({
    queryKey: ['segments-all'],
    queryFn: async () => {
      const res = await leadSegmentsAPI.getSegments({ limit: 200 });
      return res.data.data;
    },
    staleTime: 60_000,
  });

  // Pre-fill info on edit
  useEffect(() => {
    if (existing) {
      setInfo({
        name: existing.name,
        description: existing.description || '',
        segment_id: existing.segment_id,
        from_email: existing.from_email,
        from_name: existing.from_name,
        reply_to: existing.reply_to || '',
      });
    }
  }, [existing]);

  const createMutation = useMutation({
    mutationFn: (data: LeadCampaignCreate) => leadCampaignsAPI.createCampaign(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campagna creata come bozza');
      navigate(`/leads/campaigns/${res.data.id}/edit?step=2`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      leadCampaignsAPI.updateCampaign(id!, {
        name: info.name,
        description: info.description || undefined,
        segment_id: info.segment_id,
        from_email: info.from_email,
        from_name: info.from_name,
        reply_to: info.reply_to || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Info campagna aggiornate');
      setWizardStep(2);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    },
  });

  const startMutation = useMutation({
    mutationFn: () => leadCampaignsAPI.start(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campagna avviata');
      navigate(`/leads/campaigns/${id}`);
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail || "Errore nell'avvio";
      toast.error(detail);
    },
  });

  const submitInfo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!info.name.trim() || !info.segment_id || !info.from_email || !info.from_name) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (isEdit) {
      updateMutation.mutate();
    } else {
      createMutation.mutate({
        name: info.name,
        description: info.description || null,
        segment_id: info.segment_id,
        from_email: info.from_email,
        from_name: info.from_name,
        reply_to: info.reply_to || null,
      });
    }
  };

  if (isEdit && isLoadingExisting) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={() => navigate('/leads/campaigns')}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Campagne
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? `Modifica: ${existing?.name}` : 'Nuova campagna'}
        </h1>
        {existing && <LeadCampaignStateBadge state={existing.state} />}
      </div>

      {/* Stepper */}
      <ol className="flex items-center gap-3 text-sm">
        {[
          { n: 1 as const, label: 'Info base' },
          { n: 2 as const, label: 'Step email' },
          { n: 3 as const, label: 'Review & avvio' },
        ].map(({ n, label }, i) => (
          <li key={n} className="flex items-center gap-2">
            <button
              type="button"
              disabled={!isEdit && n > 1}
              onClick={() => setWizardStep(n)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                wizardStep === n
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span
                className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-semibold ${
                  wizardStep === n ? 'bg-white text-blue-600' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {n}
              </span>
              {label}
            </button>
            {i < 2 && <ArrowRight className="h-4 w-4 text-gray-400" />}
          </li>
        ))}
      </ol>

      {/* Step 1 */}
      {wizardStep === 1 && (
        <form onSubmit={submitInfo} className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Informazioni base</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nome *</label>
              <input
                type="text"
                value={info.name}
                onChange={(e) => setInfo({ ...info, name: e.target.value })}
                required
                maxLength={200}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Segmento *</label>
              <select
                value={info.segment_id}
                onChange={(e) => setInfo({ ...info, segment_id: e.target.value })}
                required
                className={inputClass}
              >
                <option value="">— Seleziona segmento —</option>
                {segments?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass}>Descrizione</label>
            <textarea
              rows={2}
              value={info.description}
              onChange={(e) => setInfo({ ...info, description: e.target.value })}
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>From email *</label>
              <input
                type="email"
                value={info.from_email}
                onChange={(e) => setInfo({ ...info, from_email: e.target.value })}
                required
                maxLength={255}
                className={inputClass}
                placeholder="info@miaazienda.it"
              />
            </div>
            <div>
              <label className={labelClass}>From name *</label>
              <input
                type="text"
                value={info.from_name}
                onChange={(e) => setInfo({ ...info, from_name: e.target.value })}
                required
                maxLength={100}
                className={inputClass}
                placeholder="Mia Azienda"
              />
            </div>
            <div>
              <label className={labelClass}>Reply-to</label>
              <input
                type="email"
                value={info.reply_to}
                onChange={(e) => setInfo({ ...info, reply_to: e.target.value })}
                maxLength={255}
                className={inputClass}
                placeholder="(opzionale)"
              />
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t">
            {canEdit && (
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {isEdit ? 'Salva e prosegui' : 'Crea bozza e prosegui'}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </form>
      )}

      {/* Step 2 */}
      {wizardStep === 2 && isEdit && (
        <StepsEditor
          campaignId={id!}
          campaignState={existing?.state}
          steps={steps || []}
          isLoading={isLoadingSteps}
          onBack={() => setWizardStep(1)}
          onNext={() => setWizardStep(3)}
          canEdit={canEdit}
        />
      )}

      {/* Step 3 */}
      {wizardStep === 3 && isEdit && existing && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Riepilogo & avvio</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex justify-between border-b border-gray-100 py-1">
              <dt className="text-gray-500">Nome</dt>
              <dd className="text-gray-900 font-medium">{existing.name}</dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-1">
              <dt className="text-gray-500">Stato</dt>
              <dd>
                <LeadCampaignStateBadge state={existing.state} />
              </dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-1">
              <dt className="text-gray-500">Segmento</dt>
              <dd className="text-gray-900">
                {segments?.find((s) => s.id === existing.segment_id)?.name || '—'}
              </dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-1">
              <dt className="text-gray-500">From</dt>
              <dd className="text-gray-900">
                {existing.from_name} &lt;{existing.from_email}&gt;
              </dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-1">
              <dt className="text-gray-500">Reply-to</dt>
              <dd className="text-gray-900">{existing.reply_to || '—'}</dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-1">
              <dt className="text-gray-500">Step configurati</dt>
              <dd className="text-gray-900 font-semibold">{steps?.length ?? 0}</dd>
            </div>
          </dl>

          {(!steps || steps.length === 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
              Devi configurare almeno uno step prima di avviare la campagna.
            </div>
          )}

          <div className="flex justify-between pt-4 border-t">
            <button
              type="button"
              onClick={() => setWizardStep(2)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Indietro
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/leads/campaigns')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Save className="h-4 w-4" />
                Salva come bozza
              </button>
              {existing.state === 'draft' ? (
                canEdit && (
                  <button
                    type="button"
                    onClick={() => startMutation.mutate()}
                    disabled={startMutation.isPending || !steps || steps.length === 0}
                    title={
                      !steps || steps.length === 0
                        ? 'Aggiungi almeno uno step prima di avviare'
                        : undefined
                    }
                    className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                  >
                    {startMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    Avvia campagna
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(`/leads/campaigns/${id}`)}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Vai al dettaglio
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface StepsEditorProps {
  campaignId: string;
  campaignState?: string;
  steps: LeadCampaignStepResponse[];
  isLoading: boolean;
  onBack: () => void;
  onNext: () => void;
  canEdit: boolean;
}

function StepsEditor({
  campaignId,
  campaignState,
  steps,
  isLoading,
  onBack,
  onNext,
  canEdit,
}: StepsEditorProps) {
  const queryClient = useQueryClient();
  const sorted = useMemo(() => [...steps].sort((a, b) => a.ordine - b.ordine), [steps]);

  const [editingStep, setEditingStep] = useState<{
    id?: string;
    ordine: number;
    delay_days: number;
    subject: string;
    body_markdown: string;
  } | null>(null);
  const [previewLeadId, setPreviewLeadId] = useState<string>('');
  const [previewSearch, setPreviewSearch] = useState('');
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const { data: leadsFound } = useQuery({
    queryKey: ['leads-search', previewSearch],
    queryFn: async () => {
      const res = await leadsAPI.getLeads({ search: previewSearch, limit: 8 });
      return res.data.data;
    },
    enabled: previewSearch.length >= 2,
  });

  const createStepMutation = useMutation({
    mutationFn: (data: typeof editingStep) =>
      leadCampaignsAPI.createStep(campaignId, {
        ordine: data!.ordine,
        delay_days: data!.delay_days,
        subject: data!.subject,
        body_markdown: data!.body_markdown,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-steps', campaignId] });
      toast.success('Step creato');
      setEditingStep(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    },
  });

  const updateStepMutation = useMutation({
    mutationFn: (data: typeof editingStep) =>
      leadCampaignsAPI.updateStep(campaignId, data!.id!, {
        ordine: data!.ordine,
        delay_days: data!.delay_days,
        subject: data!.subject,
        body_markdown: data!.body_markdown,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-steps', campaignId] });
      toast.success('Step aggiornato');
      setEditingStep(null);
    },
    onError: (error: any) => {
      const status = error.response?.status;
      if (status === 409) {
        toast.error(
          'Lo step ha già delivery inviate: clona la campagna per modificarlo (vedi documentazione).',
          { duration: 6000 }
        );
      } else {
        toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
      }
    },
  });

  const deleteStepMutation = useMutation({
    mutationFn: (stepId: string) => leadCampaignsAPI.deleteStep(campaignId, stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-steps', campaignId] });
      toast.success('Step eliminato');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const handleAddStep = () => {
    const nextOrdine = sorted.length === 0 ? 0 : sorted[sorted.length - 1].ordine + 1;
    setEditingStep({
      ordine: nextOrdine,
      delay_days: nextOrdine === 0 ? 0 : 1,
      subject: '',
      body_markdown: '',
    });
  };

  const reorderSteps = (sourceIdx: number, targetIdx: number) => {
    if (sourceIdx === targetIdx) return;
    const next = [...sorted];
    const [moved] = next.splice(sourceIdx, 1);
    next.splice(targetIdx, 0, moved);
    next.forEach((s, i) => {
      if (s.ordine !== i) {
        leadCampaignsAPI.updateStep(campaignId, s.id, { ordine: i }).catch((e: any) => {
          toast.error(`Riordino step "${s.subject}" fallito: ${e.response?.data?.detail || ''}`);
        });
      }
    });
    queryClient.invalidateQueries({ queryKey: ['campaign-steps', campaignId] });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Step della sequenza</h3>
          {canEdit && (
            <button
              type="button"
              onClick={handleAddStep}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Aggiungi step
            </button>
          )}
        </div>

        {campaignState === 'active' && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            La campagna è <strong>attiva</strong>: modificare uno step già inviato richiede una
            clonazione (il backend rifiuterà la PUT con HTTP 409).
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-6">
            Nessuno step configurato. Aggiungi il primo per iniziare.
          </div>
        ) : (
          <ul className="space-y-2">
            {sorted.map((s, idx) => (
              <li
                key={s.id}
                draggable={canEdit}
                onDragStart={() => canEdit && setDraggingIdx(idx)}
                onDragEnd={() => {
                  setDraggingIdx(null);
                  setDragOverIdx(null);
                }}
                onDragOver={(e) => {
                  if (!canEdit) return;
                  e.preventDefault();
                  setDragOverIdx(idx);
                }}
                onDragLeave={() => setDragOverIdx((v) => (v === idx ? null : v))}
                onDrop={(e) => {
                  if (!canEdit) return;
                  e.preventDefault();
                  if (draggingIdx === null) return;
                  reorderSteps(draggingIdx, idx);
                  setDraggingIdx(null);
                  setDragOverIdx(null);
                }}
                className={`border rounded-lg p-3 ${
                  dragOverIdx === idx ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200'
                } ${draggingIdx === idx ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                    title="Trascina per riordinare"
                  >
                    <GripVertical className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-gray-500">#{s.ordine + 1}</span>
                      <span className="text-sm font-medium text-gray-900">{s.subject}</span>
                      <span className="text-xs text-gray-500">
                        delay {s.delay_days} giorn{s.delay_days === 1 ? 'o' : 'i'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                      {s.body_markdown.slice(0, 200)}
                      {s.body_markdown.length > 200 ? '…' : ''}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setEditingStep({
                            id: s.id,
                            ordine: s.ordine,
                            delay_days: s.delay_days,
                            subject: s.subject,
                            body_markdown: s.body_markdown,
                          })
                        }
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        title="Modifica step"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteStepMutation.mutate(s.id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Elimina step"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {editingStep && (
          <StepEditorPanel
            campaignId={campaignId}
            stepData={editingStep}
            setStepData={setEditingStep}
            onSubmit={(data) =>
              data.id
                ? updateStepMutation.mutate(data)
                : createStepMutation.mutate(data)
            }
            isSaving={createStepMutation.isPending || updateStepMutation.isPending}
            onCancel={() => setEditingStep(null)}
            previewLeadId={previewLeadId}
            setPreviewLeadId={setPreviewLeadId}
            previewSearch={previewSearch}
            setPreviewSearch={setPreviewSearch}
            leadsFound={leadsFound || []}
          />
        )}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Indietro
        </button>
        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Prosegui
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

interface StepEditorPanelProps {
  campaignId: string;
  stepData: NonNullable<{
    id?: string;
    ordine: number;
    delay_days: number;
    subject: string;
    body_markdown: string;
  }>;
  setStepData: (
    next: {
      id?: string;
      ordine: number;
      delay_days: number;
      subject: string;
      body_markdown: string;
    } | null
  ) => void;
  onSubmit: (data: NonNullable<StepEditorPanelProps['stepData']>) => void;
  isSaving: boolean;
  onCancel: () => void;
  previewLeadId: string;
  setPreviewLeadId: (v: string) => void;
  previewSearch: string;
  setPreviewSearch: (v: string) => void;
  leadsFound: { id: string; denominazione_completa: string; email?: string | null }[];
}

function StepEditorPanel({
  campaignId,
  stepData,
  setStepData,
  onSubmit,
  isSaving,
  onCancel,
  previewLeadId,
  setPreviewLeadId,
  previewSearch,
  setPreviewSearch,
  leadsFound,
}: StepEditorPanelProps) {
  const [previewHtml, setPreviewHtml] = useState<{ subject: string; html: string } | null>(
    null
  );
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchPreview = async () => {
    if (!previewLeadId || !stepData.id) {
      toast.error(
        stepData.id
          ? 'Seleziona un lead di test'
          : 'Salva prima lo step per generare la preview'
      );
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await leadCampaignsAPI.previewStep(campaignId, stepData.id, previewLeadId);
      setPreviewHtml({ subject: res.data.subject, html: res.data.html });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nella preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30 space-y-3">
      <h4 className="text-sm font-semibold text-gray-900">
        {stepData.id ? 'Modifica step' : 'Nuovo step'}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Ordine (0 = primo)</label>
          <input
            type="number"
            min={0}
            value={stepData.ordine}
            onChange={(e) =>
              setStepData({ ...stepData, ordine: Number(e.target.value) || 0 })
            }
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Delay (giorni)</label>
          <input
            type="number"
            min={0}
            value={stepData.delay_days}
            onChange={(e) =>
              setStepData({ ...stepData, delay_days: Number(e.target.value) || 0 })
            }
            className={inputClass}
          />
        </div>
        <div className="md:col-span-1">
          <label className={labelClass}>Subject *</label>
          <input
            type="text"
            value={stepData.subject}
            onChange={(e) => setStepData({ ...stepData, subject: e.target.value })}
            maxLength={255}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>
            Body (Markdown — variabili Jinja:{' '}
            <code className="text-xs">
              {'{{lead.nome}}'}, {'{{lead.ragione_sociale}}'}
            </code>
            )
          </label>
          <textarea
            value={stepData.body_markdown}
            onChange={(e) => setStepData({ ...stepData, body_markdown: e.target.value })}
            rows={14}
            className={`${inputClass} font-mono text-xs`}
            placeholder="# Ciao {{lead.nome}}&#10;&#10;Grazie per il tuo interesse..."
          />
        </div>
        <div>
          <label className={labelClass}>Anteprima Markdown (client)</label>
          <div className="border border-gray-200 rounded-lg bg-white p-4 min-h-[280px] prose prose-sm max-w-none">
            {stepData.body_markdown ? (
              <ReactMarkdown>{stepData.body_markdown}</ReactMarkdown>
            ) : (
              <p className="text-gray-400 italic text-sm">
                Inserisci il testo Markdown a sinistra per vedere l&rsquo;anteprima qui.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Preview server-side con lead reale (solo per step già salvati) */}
      <details className="bg-white border border-gray-200 rounded-lg p-3">
        <summary className="text-sm font-medium text-gray-700 cursor-pointer flex items-center gap-2">
          <Eye className="h-4 w-4" />
          Preview server-side con un lead di test
        </summary>
        <div className="mt-3 space-y-2">
          {!stepData.id ? (
            <p className="text-xs text-amber-700">
              Salva lo step prima di poter generare la preview rendered dal backend.
            </p>
          ) : (
            <>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Cerca lead per nome/email..."
                  value={previewSearch}
                  onChange={(e) => setPreviewSearch(e.target.value)}
                  className={inputClass}
                />
                {previewSearch.length >= 2 && leadsFound.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {leadsFound.map((l) => (
                      <li key={l.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewLeadId(l.id);
                            setPreviewSearch(l.denominazione_completa);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          {l.denominazione_completa}
                          {l.email && (
                            <span className="ml-2 text-xs text-gray-500">· {l.email}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={fetchPreview}
                disabled={!previewLeadId || previewLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-xs"
              >
                {previewLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                Genera preview
              </button>
              {previewHtml && (
                <div className="border border-gray-200 rounded-lg bg-white p-3 mt-2 space-y-2">
                  <div>
                    <div className="text-xs text-gray-500">Subject</div>
                    <div className="text-sm font-semibold">{previewHtml.subject}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      Body HTML rendered (sandbox isolato)
                    </div>
                    <iframe
                      title="Anteprima email"
                      srcDoc={previewHtml.html}
                      sandbox=""
                      className="w-full min-h-[260px] border border-gray-200 rounded bg-white"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </details>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-sm"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={() => {
            if (!stepData.subject.trim() || !stepData.body_markdown.trim()) {
              toast.error('Subject e body sono obbligatori');
              return;
            }
            onSubmit(stepData);
          }}
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
          {stepData.id ? 'Salva step' : 'Crea step'}
        </button>
      </div>
    </div>
  );
}
